import React from 'react';

import AdyenCheckout from '@adyen/adyen-web';
import ApplePayButton from 'lib/components/ApplePayButton';
import axios from 'axios';
import GooglePayButton from 'lib/components/GooglePayButton';
import InfoBlock from 'lib/components/InfoBlock';
import PropTypes from 'prop-types';
import WaitingBlock from 'lib/components/WaitingBlock';
import { Trans, useTranslation } from 'react-i18next';
import styles from './Adyen.module.scss';
import styles_psp from 'lib/components/PspComposer/PspComposer.module.scss';
import '@adyen/adyen-web/dist/adyen.css';
import ModalKlarna from './components/Modal';

const PAYMENT_METHODS_REQUIRING_TO_SHOW_PAY_BUTTON = [
    'ApplePay',
    'GooglePay',
    'Klarna',
    'Paypal',
    'PayPal',
    'paypal',
];

const process = (
    pspId,
    adyen_data,
    processType,
    window,
    token,
    paymentRequestId,
    lyriaUrl,
) => {
    return axios({
        method: 'post',
        url: lyriaUrl + '/adyen/process',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        data: {
            request_token: token,
            payment_request_id: paymentRequestId,
            data: { adyen_data: adyen_data, action: processType, origin: window.location.href },
            psp: pspId,
        },
    });
};

const processAdyen = (
    processType,
    token,
    paymentRequestId,
    config,
    lyriaUrl,
    iframe,
    popup,
    handleSuccess,
    handleError,
    setCurrentView,
    onPaymentStarted,
    setDisplayDoNotLeaveWarning,
    setTriggerBancontactAppRedirection,
    userForm,
    setUserForm,
    isFormCompleted,
    setOpenModal,
    setUserFormError,
    setHasClickedKlarna,
    setIsLoading,
) => {
    return (state, dropin) => {
        const redirectOnCall = () => {
            popup.current = window.open('', 'PaymentPopup', 'dependent'); // TODO open a nice loading page instead of about:blank
            if (popup.current !== null) {
                setCurrentView('popup');
            }
        };
        if (
            state.data.paymentMethod?.type === 'klarna_account' && !isFormCompleted) {
            setOpenModal(true);
            return;
        }
        onPaymentStarted();
        setIsLoading(true);
        // note: if processType is "complete" (aka onAdditionalDetails there is not paymentMethod field in state.data)
        // note state.data.paymentMethod.type is "scheme" if payment by card, need to check the "brand" field
        // TODO set a redirect mode for a scheme paymenet method in the backend to check what's the result in /paymentConfig
        const redirectMode = 'paymentMethod' in state.data && config.redirect_modes && config.redirect_modes[state.data.paymentMethod.type];
        if (redirectMode === 'popup' && !window.IS_WEBVIEW && state.data.paymentMethod?.type !== 'klarna_account') {
            // open popup in the onclick handler
            // because it will be blocked by the browsers if we open it in the /adyen/process request callback
            redirectOnCall();
        }
        if (state.data.paymentMethod?.type === 'klarna_account') {
            state.data.klarna_form = Object.fromEntries(
                Object.entries(userForm).filter(([_key, value]) => value !== ''),
            );
        }
        process(config.id, state.data, processType, window, token, paymentRequestId, lyriaUrl)
            .then(({ data }) => {
                setHasClickedKlarna(false);
                setIsLoading(false);
                setDisplayDoNotLeaveWarning(true);
                if (popup.current) {
                    popup.current.close();
                    popup.current = null;
                }
                if (data.status === 'V' || data.status === 'P') {
                    handleSuccess(data.data);
                } else if (data.status === 'A') {
                    handleError(data.data);
                } else if (data.status === 'W' && state.data.paymentMethod?.type === 'klarna_account') {
                    setOpenModal(false);
                    redirectOnCall();
                }
            })
            .catch((error) => {
                // Manage error coming back from klarna form
                if (state.data.paymentMethod?.type === 'klarna_account') {
                    setIsLoading(false);
                    if (error.response.data.phone_number) {
                        setHasClickedKlarna(false);
                        setOpenModal(true);
                        setUserFormError(error.response.data.phone_number[0]);
                    } else {
                        setHasClickedKlarna(false);
                        setOpenModal(false);
                        setUserFormError();
                        setUserForm({
                            city: '',
                            country: '',
                            first_name: '',
                            street_number: '',
                            last_name: '',
                            zip_code: '',
                            region: '',
                            street: '',
                            phone_number: '',
                        });
                    }
                }

                const action = error?.response?.data?.data?.action;
                if (action) {
                    setDisplayDoNotLeaveWarning(true);
                    if (action.type === 'redirect' && action.method === 'GET') {
                        if (redirectMode === 'popup') {
                            if (popup.current !== null && !window.IS_WEBVIEW) {
                                setCurrentView('popup');  // not needed because currentView was already set before the request, but call anyway so that each branch has a "setCurrentView" call
                                window.open(action.url, 'PaymentPopup', 'dependent');
                            } else {
                                // if we get here (redirectMode === 'popup' && popup.current === null)
                                // it means that we can't open this payment's method in a iframe
                                // *and* that we couldn't open a popup
                                // so do a full page redirect instead...
                                setCurrentView('adyen');
                                dropin.handleAction(action);
                            }
                        } else if (redirectMode === 'host') {
                            setCurrentView('adyen');
                            dropin.handleAction(action);
                        } else {
                            iframe.current.setAttribute('src', action.url);
                            setCurrentView('iframe');
                        }
                    } else if (action.type === 'redirect' && action.method === 'POST') {
                        if (popup.current) {
                            // we don't handle popup mode for post forms yet (ever ?)
                            // but in case a payment method is misconfigured and asks us to open a popup
                            // close it
                            popup.current.close();
                            popup.current = null;
                        }

                        const iframeContent = iframe.current.contentDocument;
                        const form = iframeContent.createElement('form');
                        form.setAttribute('method', action.method);
                        form.setAttribute('action', action.url);
                        for (let key in action.data) {
                            const hiddenField = iframeContent.createElement('input');
                            hiddenField.setAttribute('type', 'hidden');
                            hiddenField.setAttribute('name', key);
                            hiddenField.setAttribute('value', action.data[key]);
                            form.appendChild(hiddenField);
                        }
                        iframeContent.documentElement.appendChild(form);
                        form.submit();
                        setCurrentView('iframe');
                    } else if (
                        action.paymentMethodType === 'bcmc_mobile'
                        && action.type === 'qrCode'
                        && action.url
                        && window.matchMedia('(max-width: 768px)').matches
                        && /iPhone|iPod/.test(navigator.userAgent)
                        && window.location !== window.top.location // is in iframe
                    ) {
                        // Work around to try to redirect to the payconiq app
                        // https://github.com/Adyen/adyen-web/blob/4193101ab34f3f2387ccae6c4af8396fff4a2ee8/packages/lib/src/components/internal/QRLoader/QRLoader.tsx#L59
                        setTriggerBancontactAppRedirection(action.url);
                        setCurrentView('adyen');
                        dropin.handleAction(action);
                    } else {
                        setCurrentView('adyen');
                        dropin.handleAction(action);
                    }
                }
            });
    };
};


/**
 * This component is one of the multiple available payment method in this project.
 * It represents the Adyen PSP integration that can deal with multiple bank card types and payment method.
 */
const Adyen = React.forwardRef(
    (
        {
            config,
            token,
            paymentRequestId,
            lyriaUrl,
            hidePayButton,
            labelPayButton, // eslint-disable-line no-unused-vars
            onPaymentStarted,
            onPaymentResult,
            loader,
            savePaymentMethod,
            savedPaymentMethods, // eslint-disable-line no-unused-vars
            paymentCardInputWarningMessage,
            operation, // eslint-disable-line no-unused-vars
            paymentMethods,
            labelPayButtonApplePayAndGooglePay,
            lang,
            themeMode,
        },
        ref,
    ) => {
        /* ------------ Initialization of the imports, the variables and the states ------------ */
        const { t } = useTranslation();

        const [currentView, setCurrentView] = React.useState('adyen');
        const container = React.useRef();
        const [isSpinnerVisible, showSpinner] = React.useState(false);
        const [attempts, setAttempts] = React.useState(0);
        const [displayDoNotLeaveWarning, setDisplayDoNotLeaveWarning] = React.useState(false);
        const [triggerBoncontactAppRedirectionUrl, setTriggerBancontactAppRedirectionUrl] = React.useState(null);
        const initialUserFormState = {
            city: '',
            country: '',
            first_name: '',
            street_number: '',
            last_name: '',
            zip_code: '',
            region: '',
            street: '',
            phone_number: '',
        };
        const [userForm, setUserForm] = React.useState(initialUserFormState);
        const [userFormError, setUserFormError] = React.useState();
        const [openModal, setOpenModal] = React.useState(false);
        const [hasClickedKlarna, setHasClickedKlarna] = React.useState(false);
        const [phoneCountryCode, setPhoneCountryCode] = React.useState('');
        const [isLoading, setIsLoading] = React.useState(false);

        const mustRecreateDropin = React.useRef(true);
        const iframe = React.useRef(null);
        const popup = React.useRef();
        const dropin = React.useRef();
        const forceShowPayButton = paymentMethods
            .every((pm) => PAYMENT_METHODS_REQUIRING_TO_SHOW_PAY_BUTTON.includes(pm.payment_method));
        const isFormCompleted = React.useMemo(() => {
            let fieldsThatMustBeFilled = {};
            if (userForm.country === 'CA' || userForm.country === 'US') {
                fieldsThatMustBeFilled = userForm;
            } else {
                const { ignoredRegion, ...otherFields } = userForm;
                fieldsThatMustBeFilled = otherFields;
            }

            return Object.entries(fieldsThatMustBeFilled).filter((elem) => {
                if (elem[0] === 'phone_number') {
                    return elem[1] === '' || elem[1] === '+';
                } else {
                    return elem[1] === '';
                }
            }).length === 0;
        }, [userForm]);

        /* ------------ The main functions ------------ */

        // The function to trigger when Adyen response is successful (payment ok).
        const handleSuccess = React.useCallback(
            (response) => {
                onPaymentResult({ success: true, data: response });
                setDisplayDoNotLeaveWarning(false);
                showSpinner(false);
            },
            [onPaymentResult],
        );

        // The function to trigger when Adyen response is not successful (payment ko).
        const handleError = React.useCallback(
            (response) => {
                if (response) {
                    mustRecreateDropin.current = true;
                    setAttempts((attempts) => attempts + 1);
                    setCurrentView('adyen');
                    onPaymentResult({ success: false, data: response });
                    setDisplayDoNotLeaveWarning(false);
                    showSpinner(false);
                }

            },
            [onPaymentResult],
        );

        // The message handler (to manage iframes)
        const postMessageHandler = React.useCallback(
            (message) => {
                if (message.origin === lyriaUrl) {
                    if (message.data.status === 'success') {
                        handleSuccess();
                    } else if (message.data.status === 'error') {
                        handleError(message.data.data);
                    }
                }
            },
            [handleError, handleSuccess, lyriaUrl],
        );

        const redirectToUrl = React.useCallback(
            (url) => () => {
                window.open(url, '_blank');
            },
            [],
        );

        /* ------------ The side effects and imperative handlers ------------ */

        // The side effect to add a listener of message posted by Adyen.
        React.useEffect(
            () => {
                window.addEventListener('message', postMessageHandler);
                return () => window.removeEventListener('message', postMessageHandler);
            },
            [postMessageHandler],
        );

        React.useEffect(() => {
            if (hasClickedKlarna) {
                mustRecreateDropin.current = true;
            }
        }, [hasClickedKlarna, userFormError]);

        // Work around to try to redirect to the payconiq app when in an iframe (safari policy that blocks redirection)
        React.useEffect(
            () => {
                if (!triggerBoncontactAppRedirectionUrl) {
                    return;
                }
                const intervalFunction = setInterval(
                    () => {
                        const bancontactButtonsAppLinkList = document.getElementsByClassName('adyen-checkout__button--qr-loader');
                        if (bancontactButtonsAppLinkList.length) {
                            const eventListener = redirectToUrl(triggerBoncontactAppRedirectionUrl);
                            bancontactButtonsAppLinkList[0].addEventListener('click', eventListener);
                            setTriggerBancontactAppRedirectionUrl(null);
                        }
                    },
                    1500,
                );
                return () => clearInterval(intervalFunction);
            },
            [triggerBoncontactAppRedirectionUrl, redirectToUrl],
        );
        // Set up the Adyen drop-in.
        React.useEffect(
            () => {
                if (config.type === 'apple_pay' && config?.useNewAppleButton) {
                    return;
                }
                if (config.type === 'google_pay' && config?.useNewGoogleButton) {
                    return;
                }
                const element = container.current;
                if (mustRecreateDropin.current) {
                    mustRecreateDropin.current = false;

                    // Apple Pay Merchant Validation (own cert by Lyria)
                    if (config.dropin_config?.paymentMethodsConfiguration?.applepay) {
                        config.dropin_config.paymentMethodsConfiguration.applepay.onValidateMerchant = (resolve, reject, validationURL) => {
                            let postData = {
                                url: validationURL,
                                domain: window.location.host,
                                merchant_id: config.apple_pay_merchant,
                            };
                            if (config.dropin_config.paymentMethodsConfiguration.applepay.configuration?.merchantIdentifier) {
                                postData['apple_merchant_id'] = config.dropin_config.paymentMethodsConfiguration.applepay.configuration.merchantIdentifier;
                            }
                            if (config.dropin_config.paymentMethodsConfiguration.applepay.configuration?.merchantName) {
                                postData['apple_merchant_name'] = config.dropin_config.paymentMethodsConfiguration.applepay.configuration.merchantName;
                            }

                            axios({
                                url: `${lyriaUrl}/payments/applepay/validatemerchant`,
                                method: 'post',
                                data: postData,
                            })
                                .then((res) => resolve(res?.data))
                                .catch((_error) => reject());
                        };
                    }
                    // Important note: this part is only computed after the first render or when we increment the attempts count (ie after a payment error)
                    // so if any component prop changes (interior mutability excluded) it will NOT be reflected in the dropin config until a payment error occurs
                    // (simplest case: `hidePayButton` changes, more complex case: `onPaymentResult` changes so we call an old callback)
                    const checkout = new AdyenCheckout({
                        ...config.dropin_config,
                        showPayButton: !hidePayButton || forceShowPayButton,
                        showStoredPaymentMethods: savePaymentMethod,
                        onSubmit: processAdyen('create', token, paymentRequestId, config, lyriaUrl, iframe, popup, handleSuccess, handleError, setCurrentView, onPaymentStarted, setDisplayDoNotLeaveWarning, setTriggerBancontactAppRedirectionUrl, userForm, setUserForm, isFormCompleted, setOpenModal, setUserFormError, setHasClickedKlarna, setIsLoading),
                        onAdditionalDetails: processAdyen('complete', token, paymentRequestId, config, lyriaUrl, iframe, popup, handleSuccess, handleError, setCurrentView, onPaymentStarted, setDisplayDoNotLeaveWarning, setTriggerBancontactAppRedirectionUrl, userForm, setUserForm, isFormCompleted, setOpenModal, setUserFormError, setHasClickedKlarna, setIsLoading),
                        onError(_error) {
                            // client-side form validation error
                            // warn the host so that it re-enables the host pay button (if there is an host pay button)
                            // note: this is triggered *a lot* but if the host follows the integration doc correctly
                            // ie by not doing anything if !res.success except re-enabling the host pay button
                            // everything should be alright
                            onPaymentResult({ success: false });
                        },
                    });
                    const newDropin = checkout.create('dropin', {
                        ...config.dropin_options, // note: dropin_options is not return by the lyria yet
                    });
                    dropin.current = newDropin;
                    newDropin.mount(element);
                    if (hasClickedKlarna) {
                        dropin.current.submit();
                    }
                }

                return () => {
                    if (mustRecreateDropin.current) {
                        dropin.current.unmount(element);
                    }
                };
            },
            [
                attempts,
                config,
                forceShowPayButton,
                hidePayButton,
                token,
                paymentRequestId,
                lyriaUrl,
                handleSuccess,
                handleError,
                onPaymentResult,
                onPaymentStarted,
                savePaymentMethod,
                userForm,
                userFormError,
                isFormCompleted,
                hasClickedKlarna,
            ],
        );

        // The handler to be able to external component to trigger 'pay' button actions.
        React.useImperativeHandle(
            ref,
            () => ({
                pay() {
                    if (dropin.current && !forceShowPayButton) {
                        dropin.current.submit();
                        // dropin.submit is asynchronous so we don't know yet if the form is filled in correctly
                        // so we return true as if it was filled in correctly
                        // `AdyenCheckout` will call the `onError` callback if the form is _not_ filled in correctly
                        // and in onError we'll warn the host
                        return true;
                    }
                },
            }),
            [forceShowPayButton],
        );
        if (config.type === 'apple_pay' && config?.useNewAppleButton) {
            let appleMerchantId = null;
            if (config.dropin_config.paymentMethodsConfiguration.applepay.configuration?.merchantIdentifier) {
                appleMerchantId = config.dropin_config.paymentMethodsConfiguration.applepay.configuration.merchantIdentifier;
            }
            let appleMerchantName = null;
            if (config.dropin_config.paymentMethodsConfiguration.applepay.configuration?.merchantName) {
                appleMerchantName = config.dropin_config.paymentMethodsConfiguration.applepay.configuration.merchantName;
            }


            return (<ApplePayButton
                config={config.config}
                merchantId={config.merchant_id}
                onLoadPaymentData={(adyen_data) => {
                    onPaymentStarted();
                    return process(config.id, adyen_data, 'create', window, token, lyriaUrl).then(({ data }) => {
                        if (popup.current) {
                            popup.current.close();
                            popup.current = null;
                        }

                        if (data.status === 'V') {
                            handleSuccess(data.data);
                        } else if (data.status === 'A') {
                            handleError(data.data);
                        }
                        // it’s important to fit «data» into a dict with «data» key because
                        // Our ApplePay button expects this
                        return { data: data };
                    });
                }}
                labelPayButton={labelPayButtonApplePayAndGooglePay}
                lyriaUrl={lyriaUrl}
                lang={lang}
                themeMode={themeMode}
                isDisabled={false}
                appleMerchantId={appleMerchantId}
                appleMerchantName={appleMerchantName}
            />);
        } else if (config.type === 'google_pay' && config?.useNewGoogleButton) {
            // the GooglePay payment method selector is closed as soon as the user clicks "Pay"
            // so we have to show a spinner ourselves while waiting for backend validation
            if (isSpinnerVisible) {
                return (
                    <WaitingBlock loader={loader}>
                        <div className="wz-lyriapay__payline__waiting-block--message">
                            {t('waitingPaymentResult')}
                        </div>
                        {displayDoNotLeaveWarning && (
                            <div className="wz-lyriapay__payline__waiting-block--message-do-not-leave">
                                {t('waitingPaymentResultDoNotLeavePage')}
                            </div>
                        )}
                    </WaitingBlock>
                );
            } else {
                return (
                    <GooglePayButton
                        config={config.config}
                        onLoadPaymentData={(adyen_data) => {
                            onPaymentStarted();
                            showSpinner(true);
                            process(
                                config.id,
                                adyen_data,
                                'create',
                                window,
                                token,
                                lyriaUrl,
                            ).then(
                                ({ data }) => {
                                    if (popup.current) {
                                        popup.current.close();
                                        popup.current = null;
                                    }

                                    if (data.status === 'V') {
                                        handleSuccess(data.data);
                                    } else if (data.status === 'A') {
                                        handleError(data.data);
                                    }
                                },
                            );
                        }}
                        labelPayButton={labelPayButtonApplePayAndGooglePay}
                        lang={lang}
                        themeMode={themeMode}
                        isDisabled={false}
                    />
                );
            }
        }

        /* ------------ The JSX to return according to the logic above ------------ */
        return (
            <div id="adyen">
                <ModalKlarna
                    amount={config.dropin_config.amount.value}
                    currency={config.dropin_config.amount.currency}
                    userForm={userForm}
                    userFormError={userFormError}
                    setUserFormError={setUserFormError}
                    setUserForm={setUserForm}
                    initialUserFormState={initialUserFormState}
                    phoneCountryCode={phoneCountryCode}
                    setPhoneCountryCode={setPhoneCountryCode}
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                    isFormCompleted={isFormCompleted}
                    setHasClickedKlarna={setHasClickedKlarna}
                    mustRecreateDropin={mustRecreateDropin}
                    isLoading={isLoading}
                />
                {
                    displayDoNotLeaveWarning && (
                        <div className={styles.container}>
                            <WaitingBlock loader={null}>
                                <div className={styles.message}>
                                    <Trans i18nKey="waitingPaymentResultDoNotLeavePage" />
                                </div>
                            </WaitingBlock>
                        </div>
                    )
                }
                <div
                    ref={container}
                    style={
                        currentView === 'adyen'
                            ? { display: 'block' }
                            : { display: 'none' }
                    }
                />
                {
                    !!paymentCardInputWarningMessage && currentView === 'adyen' && (
                        <div className={styles.warning_auto_top_up_save_payment_method}>
                            <InfoBlock
                                title={t('payment_card_input_warning_message_title')}
                                message={paymentCardInputWarningMessage}
                            />
                        </div>
                    )
                }
                {
                    currentView === 'popup' && (
                        <WaitingBlock loader={loader}>
                            <div>
                                <Trans i18nKey="adyenPopupProcess" />
                            </div>
                        </WaitingBlock>
                    )
                }
                <iframe
                    className={styles_psp.common_iframe}
                    title="iframe"
                    ref={iframe}
                    /* recreate a new iframe for each time we try the payment */
                    key={`iframe${attempts}`}
                    style={currentView === 'iframe' ? {} : { display: 'none' }}
                />
            </div>
        );
    },
);

Adyen.propTypes = {
    /**
     * The specific configuration related to this payment method (IBAN).
     */
    config: PropTypes.object,
    /**
     * The token to use in order to be able to call the backend when we submit the form data.
     */
    token: PropTypes.string,
    /**
     * The paymentRequestID to use in order to be able to call the backend when we submit the data in the paymentPage.
     */
    paymentRequestId: PropTypes.string,
    /**
     * The backend url to use to pass the form data.
     */
    lyriaUrl: PropTypes.string.isRequired,
    /**
     * Whether the submit button must be displayed or not.
     */
    hidePayButton: PropTypes.bool,
    /**
     * The label of the 'pay' button to display. hidePayButton props must be false of course.
     */
    labelPayButton: PropTypes.string,
    /**
     * The label of the 'pay' button to display for GogglePay and ApplePay.
     */
    labelPayButtonApplePayAndGooglePay: PropTypes.oneOf([
        'buy',
        'book',
        'donate',
        'order',
        'pay',
        'logoOnly',
    ]),
    /**
     * Whether the expiry date of the credit card must be set in a text input instead of a dropdown.
     */
    expiryAsText: PropTypes.bool,
    /**
     * The callback function to trigger when the payment process is starting (to manage Payline exchanges).
     */
    onPaymentStarted: PropTypes.func.isRequired,
    /**
     * The callback function to trigger when the backend return the result of the form data submit.
     */
    onPaymentResult: PropTypes.func.isRequired,
    /**
     * The loader component to use when the payment or drop-in is loading.
     */
    loader: PropTypes.node,
    /**
     * Whether we want to show the checkbox to save the payment method.
     */
    savePaymentMethod: PropTypes.bool,
    /**
     * The list of saved payment methods available.
     * The goal is to be able to select one of them and avoid typing it again).
     */
    savedPaymentMethods: PropTypes.array,
    /**
     * A warning message to display for payment at the card input step (mainly to advise the user to save its payment method).
     */
    paymentCardInputWarningMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     * The operation type to process.
     */
    operation: PropTypes.oneOf(['credit', 'payment', 'refund']),
    /**
     * The list of payment methods that belongs to the given payment method. That's a kind of sub payment method.
     * For example, for 'credit card' payment method we could have Visa, Mastercard and so on.
     */
    paymentMethods: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            payment_method: PropTypes.string.isRequired,
            is_card: PropTypes.bool,
        }),
    ),
    /**
     * The language of the user in order to display the appropriate translated drop-in (for Apple pay).
     */
    lang: PropTypes.string,
    /**
     * The theme to use (dark or light).
     */
    themeMode: PropTypes.string,
};

Adyen.defaultProps = {
    config: {},
    hidePayButton: false,
    labelPayButton: null,  // Fallback on 'pay' or 'ask refund' label.
    expiryAsText: true,
    loader: null,
    savePaymentMethod: false,
    savedPaymentMethods: [],
    paymentCardInputWarningMessage: null,
    paymentMethods: [],
};

Adyen.displayName = 'Adyen';

export default Adyen;
